import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

/**
 * PandiFullscreenReel
 *
 * Cinematic 16:9 protagonist reel for "RAGHAVA LAWRENCE AS PANDI".
 * Auto-plays WITH AUDIO when scrolled into view. On mobile (especially
 * iOS Safari) unmuted autoplay is restricted, so we:
 *   1) Serve a much smaller mobile-optimised MP4 (~12 MB, 720p, ~1 Mbps)
 *      so playback never stalls on cellular connections.
 *   2) Start the video MUTED first (always allowed) so visuals never stop.
 *   3) Try to enable audio after a moment / after the section is in view.
 *   4) Show a prominent "TAP FOR SOUND" pill if audio is still blocked,
 *      so the user gets one obvious gesture to unmute.
 *
 * Sources are ordered so each browser/device picks the lightest playable
 * file:
 *   - <source media="(max-width: 900px)" src=mobile.mp4 type=mp4/h264>  ← phones
 *   - <source src=pandi-reel.webm type=webm/vp9>                       ← Chromium
 *   - <source src=pandi-reel.mp4 type=mp4/h264>                        ← desktop Safari
 */
export default function PandiFullscreenReel({
  src = '/assets/video/pandi-reel.mp4',
  mobileSrc = '/assets/video/pandi-reel-mobile.mp4',
  webm = '/assets/video/pandi-reel.webm',
  poster = '/assets/moodboard/pandi2.png',
}) {
  const videoRef = useRef(null);
  const wrapperRef = useRef(null);
  const [muted, setMuted] = useState(true); // start muted so visuals always play
  const [hasStarted, setHasStarted] = useState(false);

  // Try to enable audio playback. Safe to call multiple times.
  const tryUnmute = async () => {
    const v = videoRef.current;
    if (!v) return false;
    try {
      v.muted = false;
      v.volume = 1.0;
      await v.play();
      setMuted(false);
      return true;
    } catch (_) {
      // Audio still blocked — keep muted, surface the tap-to-unmute UI
      v.muted = true;
      setMuted(true);
      try { await v.play(); } catch (__) {}
      return false;
    }
  };

  // Start playback (muted) — always safe in modern browsers when playsInline.
  const startPlayback = async () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      v.muted = true;
      await v.play();
      setHasStarted(true);
      // Immediately attempt unmute — works on Chrome desktop & most Android.
      // On iOS Safari this almost always fails; user taps the pill.
      tryUnmute();
    } catch (_) {
      // Even muted autoplay blocked (rare) — first user tap will start it.
    }
  };

  // IntersectionObserver — start playback when section comes into view,
  // pause when fully scrolled past.
  useEffect(() => {
    const el = wrapperRef.current;
    const v = videoRef.current;
    if (!el || !v) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            startPlayback();
          } else {
            try { v.pause(); } catch (_) {}
          }
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Global "first user gesture" listener — on iOS the password-gate tap
  // doesn't transfer audio permission to a later <video>, so we wait for
  // ANY tap/touch on the page and use that gesture to unmute.
  useEffect(() => {
    const handler = () => {
      const v = videoRef.current;
      if (!v) return;
      if (v.muted) tryUnmute();
    };
    window.addEventListener('touchstart', handler, { passive: true, once: false });
    window.addEventListener('pointerdown', handler, { passive: true, once: false });
    return () => {
      window.removeEventListener('touchstart', handler);
      window.removeEventListener('pointerdown', handler);
    };
  }, []);

  const handleUnmuteTap = (e) => {
    e?.stopPropagation?.();
    tryUnmute();
  };

  return (
    <div ref={wrapperRef} className="relative w-full" data-testid="pandi-fullscreen-reel">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: 'easeOut' }}
        viewport={{ once: true, amount: 0.2 }}
        className="relative w-full aspect-video overflow-hidden rounded-xl gold-border shadow-[0_30px_80px_rgba(0,0,0,0.7)]"
      >
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover"
          playsInline
          webkit-playsinline="true"
          x5-playsinline="true"
          loop
          muted
          preload="auto"
          poster={poster}
          data-testid="pandi-reel-video"
        >
          {/* Mobile devices / narrow viewports — small file, smooth on 4G */}
          <source media="(max-width: 900px)" src={mobileSrc} type="video/mp4" />
          {/* Chromium / Firefox desktop — VP9 webm */}
          <source src={webm} type="video/webm" />
          {/* Desktop Safari & full quality fallback */}
          <source src={src} type="video/mp4" />
        </video>

        {/* Vignette overlay (very subtle) */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(120% 80% at 50% 50%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.45) 100%)',
          }}
        />

        {/* Corner crosshairs */}
        <span className="pointer-events-none absolute top-3 left-3 w-5 h-5 border-l border-t border-[color:var(--gold)]/70" />
        <span className="pointer-events-none absolute top-3 right-3 w-5 h-5 border-r border-t border-[color:var(--gold)]/70" />
        <span className="pointer-events-none absolute bottom-3 left-3 w-5 h-5 border-l border-b border-[color:var(--gold)]/70" />
        <span className="pointer-events-none absolute bottom-3 right-3 w-5 h-5 border-r border-b border-[color:var(--gold)]/70" />

        {/* Prominent TAP FOR SOUND pill — shown whenever audio is still muted.
            Larger / clearer on mobile, gentle on desktop. */}
        {muted && (
          <motion.button
            type="button"
            onClick={handleUnmuteTap}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute bottom-5 right-5 z-10 flex items-center gap-2 px-4 py-2.5 rounded-full bg-black/75 border border-[color:var(--gold)]/70 hover:border-[color:var(--gold)] active:scale-[0.97] transition-[transform,border-color] duration-150 touch-manipulation"
            style={{ minHeight: '44px' }}
            data-testid="pandi-reel-unmute"
            aria-label="Tap for sound"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-[color:var(--gold)]">
              <path d="M11 5L6 9H2v6h4l5 4V5z" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
            <span className="font-ui text-[10px] sm:text-[11px] tracking-[0.35em] uppercase text-[color:var(--gold)]">
              Tap for Sound
            </span>
          </motion.button>
        )}
      </motion.div>

      {/* Footer strip */}
      <div className="mt-3 flex items-center justify-between font-ui text-[10px] tracking-[0.32em] uppercase">
        <span className="text-[color:var(--text-dim)]">REEL 01 · CHARACTER INTRODUCTION</span>
        <span className="text-[color:var(--gold)]">BLACK AXE · 2026</span>
      </div>
    </div>
  );
}
